import React, {useEffect, useState} from "react";
import MoodChart from "../MoodChart";
import {saveChosenPseudo} from "../chosenPseudo";
import {apiGetVisitorMoods, apiPostVisitorMood, apiGetPseudoDraw} from "../../api/landing";
import {generateAvatar} from "../../utils";

// Humeur des visiteurs : un clic, sans compte. Compteur SÉPARÉ de l'humeur des agents (voir le serveur : table dédiée,
// aucune donnée personnelle). Un vote par jour et par navigateur est mémorisé ici (stockage local, facultatif).
const VOTE_KEY = "paroles_agents_humeur_visiteur";
const VISITOR_MOODS = [
    {level: 1, emoji: "😫", label: "Très difficile"}, {level: 2, emoji: "😟", label: "Difficile"}, {level: 3, emoji: "😐", label: "Correct"},
    {level: 4, emoji: "😊", label: "Bien"}, {level: 5, emoji: "🤩", label: "Super"},
];
// Jour calendaire à Paris (et non UTC : sinon un revote serait possible dès 1 h ou 2 h du matin heure de Paris).
export const parisDayKey = (date) => date.toLocaleDateString("sv-SE", {timeZone: "Europe/Paris"});
const todayKey = () => parisDayKey(new Date());

function votedToday() {
    try {
        return localStorage.getItem(VOTE_KEY) === todayKey();
    } catch {
        return false;
    }
}

function rememberVote() {
    try {
        localStorage.setItem(VOTE_KEY, todayKey());
    } catch {
        // stockage indisponible : le vote reste possible demain comme aujourd'hui
    }
}

export function VisitorMoodBlock({data, ctx}) {
    const [phase, setPhase] = useState(() => (votedToday() ? "results" : "choose"));
    const [tally, setTally] = useState(undefined); // undefined = pas chargé, null = indisponible
    const [error, setError] = useState("");
    const [justVoted, setJustVoted] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (phase !== "results") return undefined;
        let cancelled = false;
        Promise.resolve(apiGetVisitorMoods()).then(result => { if (!cancelled) setTally(result || null); });
        return () => { cancelled = true; };
    }, [phase, attempt]);

    function retryResults() {
        setTally(undefined);
        setAttempt(a => a + 1);
    }

    async function vote(level) {
        setError("");
        const ok = await apiPostVisitorMood(level);
        if (!ok) {
            setError("Impossible d'enregistrer votre vote pour le moment. Réessayez plus tard.");
            return;
        }
        rememberVote();
        setJustVoted(true);
        setPhase("results");
    }

    return (
        <div className="block-visitor-mood custom-card">
            <h2 className="serif block-heading">{data.title || "Et vous, comment ça va aujourd'hui ?"}</h2>
            {data.intro && <p>{data.intro}</p>}
            {phase === "choose" ? (
                <>
                    <div className="mood-choices" role="group" aria-label="Votre humeur du jour">
                        {VISITOR_MOODS.map(m => (
                            <button key={m.level} type="button" className="mood-choice" aria-label={m.label} onClick={() => vote(m.level)}>
                                <span aria-hidden="true">{m.emoji}</span>
                            </button>
                        ))}
                    </div>
                    {error && <p role="alert" className="block-visitor-mood__error">{error}</p>}
                </>
            ) : (
                <>
                    <p>{justVoted ? "Merci pour votre vote." : "Vous avez déjà voté aujourd'hui."}</p>
                    {tally && tally.results && <MoodChart moods={tally}/>}
                    {tally && tally.belowThreshold && <p>Pas assez de votes pour afficher les résultats ({tally.threshold} minimum).</p>}
                    {tally === null && (
                        <p>
                            Les résultats ne sont pas disponibles pour le moment.{" "}
                            <button type="button" className="btn btn-ghost" onClick={retryResults}>Réessayer</button>
                        </p>
                    )}
                    <p>Inscrivez-vous pour que votre vote compte avec ceux des agents.</p>
                    <button type="button" className="btn btn-primary" onClick={ctx.onRegister}>S'inscrire</button>
                </>
            )}
        </div>
    );
}

// « Quel animal serez-vous ? » : un seul appel renvoie 5 pseudos libres ; le navigateur les fait défiler, puis reboucle
// sur le premier sans nouvel appel (pour d'autres pseudos : recharger la page). Aucune réservation.
export function AnimalDrawBlock({data, ctx}) {
    const [pseudos, setPseudos] = useState(null);
    const [index, setIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    async function draw() {
        setLoading(true);
        setFailed(false);
        const result = await apiGetPseudoDraw();
        setLoading(false);
        if (!Array.isArray(result) || result.length === 0) {
            setFailed(true);
            return;
        }
        setPseudos(result);
        setIndex(0);
    }

    function register() {
        saveChosenPseudo(pseudos[index]);
        ctx.onRegister();
    }

    const current = pseudos ? pseudos[index] : null;
    const avatar = current ? generateAvatar(current.pseudo) : null;

    return (
        <div className="block-animal-draw custom-card">
            <h2 className="serif block-heading">{data.title || "Quel animal serez-vous ?"}</h2>
            {!current ? (
                <div className="block-animal-draw__actions">
                    <button type="button" className="btn btn-primary" onClick={draw} disabled={loading}>
                        {data.button_label || "Découvrir mon animal"}
                    </button>
                    {/* Le tirage compte comme bouton d'inscription pour le garde de publication : l'inscription reste
                        possible avant le tirage et si le tirage échoue. */}
                    <button type="button" className="btn btn-ghost" onClick={ctx.onRegister}>S'inscrire</button>
                </div>
            ) : (
                <div className="block-animal-draw__result">
                    <div className="block-animal-draw__avatar" style={{backgroundColor: current.couleur}}>
                        {avatar && avatar.image
                            ? <img src={avatar.image} alt="" loading="lazy"/>
                            : <span aria-hidden="true">{current.emoji}</span>}
                    </div>
                    <strong className="serif block-animal-draw__name">{current.pseudo}</strong>
                    <div className="block-animal-draw__actions">
                        <button type="button" className="btn btn-ghost" onClick={() => setIndex((index + 1) % pseudos.length)}>Un autre pseudo</button>
                        <button type="button" className="btn btn-primary" onClick={register}>S'inscrire avec ce pseudo</button>
                    </div>
                </div>
            )}
            {failed && <p role="status">Le tirage est indisponible pour le moment. Réessayez plus tard.</p>}
        </div>
    );
}
